import React, { useState, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { 
  TrendingUp, TrendingDown, ChevronDown, ChevronUp,
  Plus, CheckCircle2, AlertTriangle, XCircle, Calendar, Clock
} from 'lucide-react';
import { DataLogModal } from './DataLogModal';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine
} from 'recharts';

/**
 * Format a value based on metric format
 */
const formatValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  
  switch (format) {
    case 'currency':
      return `$${num.toLocaleString()}`;
    case 'percent':
      return `${num}%`;
    case 'time':
      if (num >= 60) {
        const hours = Math.floor(num / 60);
        const mins = Math.round(num % 60);
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
      }
      return `${num}m`;
    default:
      return num.toLocaleString();
  }
};

/**
 * Get status badge config based on pace status (projection-based)
 * 
 * Status types:
 * - no_data: 0 data points logged
 * - insufficient_data: < min points (can't calculate trend)
 * - on_track: Projected to hit target
 * - off_track: Projected to miss target
 * - target_hit: Progress >= 100%
 */
const getStatusBadge = (paceStatus, progress) => {
  // Check for target hit first
  if (progress >= 100) {
    return { 
      label: 'Target Hit!', 
      color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
      icon: CheckCircle2
    };
  }
  
  // Use pace status from backend projection
  switch (paceStatus) {
    case 'on_track':
      return { 
        label: 'On Track', 
        color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
        icon: TrendingUp
      };
    
    case 'off_track':
      return { 
        label: 'Off Track', 
        color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
        icon: XCircle
      };
    
    case 'insufficient_data':
      return { 
        label: 'Insufficient Data', 
        color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
        icon: AlertTriangle
      };
    
    case 'no_data':
    default:
      return { 
        label: 'No Data', 
        color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
        icon: null 
      };
  }
};

/**
 * Calculate measurement schedule info
 */
const getMeasurementInfo = (measurementInterval, dataPoints, goalStartDate, targetDate) => {
  const now = new Date();
  const sortedPoints = [...dataPoints].sort((a, b) => new Date(b.date) - new Date(a.date));
  const lastEntry = sortedPoints.length > 0 ? new Date(sortedPoints[0].date) : null;
  
  // Calculate interval in days
  const intervalDays = {
    'daily': 1,
    'weekly': 7,
    'biweekly': 14,
    'monthly': 30,
    'quarterly': 90
  }[measurementInterval] || 7;
  
  // Calculate next due date
  let nextDue;
  if (lastEntry) {
    nextDue = new Date(lastEntry);
    nextDue.setDate(nextDue.getDate() + intervalDays);
  } else if (goalStartDate) {
    nextDue = new Date(goalStartDate);
  } else {
    nextDue = new Date();
  }
  
  // Check if overdue
  const isOverdue = nextDue < now;
  const daysDiff = Math.ceil((nextDue - now) / (1000 * 60 * 60 * 24));
  
  // Format next due
  const formatNextDue = () => {
    if (isOverdue) {
      const overdueDays = Math.abs(daysDiff);
      if (overdueDays === 0) return 'Due today';
      if (overdueDays === 1) return 'Overdue by 1 day';
      return `Overdue by ${overdueDays} days`;
    }
    if (daysDiff === 0) return 'Due today';
    if (daysDiff === 1) return 'Due tomorrow';
    if (daysDiff <= 7) return `Due in ${daysDiff} days`;
    return `Due ${nextDue.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };
  
  return {
    interval: measurementInterval || 'weekly',
    nextDue: formatNextDue(),
    isOverdue,
    lastEntry: lastEntry ? lastEntry.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null
  };
};

/**
 * Custom Tooltip for the chart
 */
const CustomChartTooltip = ({ active, payload, label, metricFormat, colors }) => {
  if (!active || !payload || !payload.length) return null;
  
  return (
    <div className={cn(
      "p-3 rounded-lg shadow-lg border",
      colors?.bgCard || "bg-white dark:bg-gray-900",
      colors?.borderPrimary || "border-gray-200 dark:border-gray-700"
    )}>
      <p className={cn("text-xs mb-1", colors?.textMuted || "text-gray-500")}>{label}</p>
      <p className={cn("text-lg font-bold", colors?.textPrimary || "text-gray-900 dark:text-white")}>
        {formatValue(payload[0].value, metricFormat)}
      </p>
      {payload[0].payload.note && (
        <p className={cn("text-xs mt-1 max-w-[200px]", colors?.textSecondary || "text-gray-600 dark:text-gray-400")}>
          {payload[0].payload.note}
        </p>
      )}
    </div>
  );
};

// Get confidence fill color
const getConfidenceFill = (confidence) => {
  switch (confidence) {
    case 'high': return '#22c55e'; // green-500
    case 'low': return '#ef4444'; // red-500
    default: return '#f59e0b'; // amber-500 (medium)
  }
};

// Custom dot component with confidence-based colors
const ChartDot = (props) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null || payload?.value == null) return null;
  
  const fillColor = payload.isStart ? '#94a3b8' : getConfidenceFill(payload.confidence);
  
  return (
    <circle 
      cx={cx} 
      cy={cy} 
      r={4} 
      fill={fillColor} 
      stroke="#fff" 
      strokeWidth={2}
    />
  );
};

// Custom active dot
const ChartActiveDot = (props) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;
  
  const fillColor = payload?.isStart ? '#94a3b8' : getConfidenceFill(payload?.confidence);
  
  return (
    <circle 
      cx={cx} 
      cy={cy} 
      r={6} 
      fill={fillColor} 
      stroke="#fff" 
      strokeWidth={2}
    />
  );
};

/**
 * Recharts-based KPI Chart with Goal Line and Timeline
 */
const KPIChart = ({ 
  dataPoints = [], 
  startValue, 
  targetValue,
  metricFormat,
  direction = 'higher_is_better',
  colors,
  goalStartDate,
  targetDate,
  height = 220 
}) => {
  // Build timeline from goal start to target date
  const chartData = useMemo(() => {
    const points = [];
    const startDt = goalStartDate ? new Date(goalStartDate) : null;
    const endDt = targetDate ? new Date(targetDate) : null;
    
    // Create a map of existing data points by date
    const dataMap = {};
    dataPoints.forEach(p => {
      const dateKey = new Date(p.date).toISOString().split('T')[0];
      dataMap[dateKey] = p;
    });
    
    // If we have both start and end dates, create timeline
    if (startDt && endDt) {
      // Add start point
      const startKey = startDt.toISOString().split('T')[0];
      points.push({
        date: startDt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        dateRaw: startDt,
        value: dataMap[startKey]?.value ?? startValue ?? null,
        note: dataMap[startKey]?.note,
        isStart: true
      });
      
      // Add data points that fall between start and end
      const sortedData = [...dataPoints]
        .filter(p => {
          const d = new Date(p.date);
          return d > startDt && d < endDt;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      sortedData.forEach(p => {
        points.push({
          date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          dateRaw: new Date(p.date),
          value: p.value,
          note: p.note,
          confidence: p.confidence || 'medium'
        });
      });
      
      // Add end/target point
      const endKey = endDt.toISOString().split('T')[0];
      points.push({
        date: endDt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        dateRaw: endDt,
        value: dataMap[endKey]?.value ?? null,
        note: dataMap[endKey]?.note,
        confidence: dataMap[endKey]?.confidence || 'medium',
        isTarget: true,
        targetValue: targetValue
      });
    } else {
      // Fallback: just use data points
      const sorted = [...dataPoints].sort((a, b) => new Date(a.date) - new Date(b.date));
      sorted.forEach(p => {
        points.push({
          date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          dateRaw: new Date(p.date),
          value: p.value,
          note: p.note,
          confidence: p.confidence || 'medium'
        });
      });
    }
    
    return points;
  }, [dataPoints, goalStartDate, targetDate, startValue, targetValue]);
  
  // Calculate Y-axis domain
  const yDomain = useMemo(() => {
    const allValues = [startValue, targetValue, ...dataPoints.map(p => p.value)].filter(v => v != null);
    if (allValues.length === 0) return [0, 100];
    
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.15 || 10;
    
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  }, [dataPoints, startValue, targetValue]);
  
  // Filter out null values for the line (keeps timeline intact)
  const lineData = chartData.filter(d => d.value != null);
  
  if (chartData.length === 0) {
    return (
      <div className={cn(
        "flex items-center justify-center rounded-lg border border-dashed",
        colors?.borderPrimary || "border-gray-200 dark:border-gray-700"
      )} style={{ height }}>
        <p className={cn("text-sm", colors?.textMuted || "text-gray-400")}>
          No timeline available. Set a target date on the goal.
        </p>
      </div>
    );
  }
  
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 25, right: 30, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
          <XAxis 
            dataKey="date" 
            fontSize={10}
            tick={{ fill: '#9ca3af' }}
            interval="preserveStartEnd"
          />
          <YAxis 
            fontSize={10}
            domain={yDomain}
            tickFormatter={(v) => formatValue(v, metricFormat)}
            tick={{ fill: '#9ca3af' }}
            width={45}
          />
          <Tooltip content={<CustomChartTooltip metricFormat={metricFormat} colors={colors} />} />
          
          {/* Start value reference line */}
          {startValue != null && (
            <ReferenceLine 
              y={startValue} 
              stroke="#94a3b8" 
              strokeDasharray="4 4"
              strokeWidth={1}
            />
          )}
          
          {/* Target/Goal reference line */}
          {targetValue != null && (
            <ReferenceLine 
              y={targetValue} 
              stroke="#22c55e" 
              strokeDasharray="6 3"
              strokeWidth={2}
              label={{ 
                value: `Target: ${formatValue(targetValue, metricFormat)}`, 
                position: 'top',
                fontSize: 11,
                fill: '#22c55e',
                fontWeight: 600
              }}
            />
          )}
          
          {/* Data line - only connects non-null values */}
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke="#6366f1" 
            strokeWidth={2}
            dot={ChartDot}
            activeDot={ChartActiveDot}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Mini Sparkline for collapsed state - only renders when there's data
 */
const MiniSparkline = ({ dataPoints = [] }) => {
  if (dataPoints.length === 0) return null;
  
  const sorted = [...dataPoints].sort((a, b) => new Date(a.date) - new Date(b.date));
  const values = sorted.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  
  const points = sorted.map((d, i) => {
    const x = sorted.length === 1 ? 25 : (i / (sorted.length - 1)) * 50;
    const y = 20 - ((d.value - min) / range) * 16;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox="0 0 50 24" className="w-12 h-6 flex-shrink-0">
      <polyline
        points={points}
        fill="none"
        stroke="#6366f1"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/**
 * Active KPI Card Component - Chart-first view for Active Goals
 */
export const ActiveKPICard = ({
  goalId,
  kpi,
  computed,
  paceDetail,  // New: pace detail from calculate_goal_pace
  onRefresh,
  colors = {},
  goalStartDate,
  targetDate,
  defaultExpanded = false, // Default to collapsed for Active goals
  aggregateMethodLabel, // The aggregate method name to prefix status label
  isLoading = false // Loading state
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showDataModal, setShowDataModal] = useState(false);
  
  // Show loading skeleton
  if (isLoading || !kpi) {
    return (
      <div className={cn(
        "rounded-xl border p-4 animate-pulse",
        colors.borderPrimary || "border-gray-200 dark:border-gray-700",
        colors.bgCard || "bg-white dark:bg-gray-900"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className={cn("h-4 w-48 rounded", colors.bgTertiary || "bg-gray-200 dark:bg-gray-700")} />
            <div className={cn("h-3 w-32 rounded mt-2", colors.bgTertiary || "bg-gray-200 dark:bg-gray-700")} />
          </div>
          <div className={cn("h-6 w-24 rounded-full", colors.bgTertiary || "bg-gray-200 dark:bg-gray-700")} />
        </div>
      </div>
    );
  }
  
  const { 
    metric_name, metric_format, direction, start_value, target_value, 
    current_value, data_points = [], measurement_interval 
  } = kpi;
  
  // Get pace status from backend (projection-based)
  const paceStatus = paceDetail?.pace_status || 'no_data';
  
  // Progress percentage
  const progressPercent = paceDetail?.progress_pct ?? computed?.progress ?? 0;
  
  const statusBadge = getStatusBadge(paceStatus, progressPercent);
  const StatusIcon = statusBadge.icon;
  const DirectionIcon = direction === 'lower_is_better' ? TrendingDown : TrendingUp;
  
  // Build status label with method prefix
  const statusLabel = aggregateMethodLabel 
    ? `${aggregateMethodLabel}: ${statusBadge.label}` 
    : statusBadge.label;
  
  // Get latest value from data points or current_value
  const sortedPoints = [...data_points].sort((a, b) => new Date(b.date) - new Date(a.date));
  const latestValue = sortedPoints.length > 0 ? sortedPoints[0].value : current_value;
  
  // Get measurement schedule info
  const measurementInfo = getMeasurementInfo(
    measurement_interval, 
    data_points, 
    goalStartDate,
    targetDate
  );
  
  return (
    <>
      <div 
        className={cn(
          "rounded-xl border transition-all duration-200",
          isExpanded ? "shadow-lg" : "shadow-sm hover:shadow-md",
          colors.bgCard || "bg-white dark:bg-gray-900",
          colors.borderPrimary || "border-gray-200 dark:border-gray-700"
        )}
      >
        {/* Header - Always visible */}
        <div 
          className={cn(
            "flex items-center gap-3 p-4 cursor-pointer",
            isExpanded && "border-b",
            colors.borderPrimary || "border-gray-200 dark:border-gray-700"
          )}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {/* Expand/Collapse indicator */}
          <div className={cn("p-1.5 rounded-lg", colors.bgTertiary || "bg-gray-100 dark:bg-gray-800")}>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </div>
          
          {/* Metric name and direction */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className={cn("font-semibold truncate", colors.textPrimary || "text-gray-900 dark:text-white")}>
                {metric_name || 'Unnamed KPI'}
              </h4>
              <DirectionIcon className={cn(
                "w-4 h-4 flex-shrink-0",
                direction === 'lower_is_better' ? "text-blue-500" : "text-green-500"
              )} />
            </div>
            
            {/* Collapsed: Show key numbers with sparkline on the right */}
            {!isExpanded && (
              <div className="flex items-center gap-3 mt-1">
                <span className={cn("text-sm", colors.textMuted || "text-gray-500")}>
                  {formatValue(start_value, metric_format)} → {latestValue != null ? formatValue(latestValue, metric_format) : '-'} → {formatValue(target_value, metric_format)}
                  {progressPercent > 0 && (
                    <span className="ml-1.5 font-bold text-indigo-600 dark:text-indigo-400">@{progressPercent}%</span>
                  )}
                </span>
                <MiniSparkline dataPoints={data_points} />
              </div>
            )}
          </div>
          
          {/* Status Badge */}
          <div className={cn(
            "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
            statusBadge.color
          )}>
            {StatusIcon && <StatusIcon className="w-3.5 h-3.5" />}
            {statusLabel}
          </div>
        </div>
        
        {/* Expanded Content */}
        {isExpanded && (
          <div className="p-4 space-y-4">
            {/* Measurement Schedule Info */}
            <div className={cn(
              "flex items-center justify-between px-3 py-2 rounded-lg text-sm",
              measurementInfo.isOverdue 
                ? "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
                : cn(colors.bgTertiary || "bg-gray-50 dark:bg-gray-800/50")
            )}>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <Clock className={cn("w-4 h-4", colors.textMuted || "text-gray-400")} />
                  <span className={cn("capitalize", colors.textSecondary || "text-gray-600 dark:text-gray-400")}>
                    {measurementInfo.interval} updates
                  </span>
                </div>
                {measurementInfo.lastEntry && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className={cn("w-4 h-4", colors.textMuted || "text-gray-400")} />
                    <span className={colors.textSecondary || "text-gray-600 dark:text-gray-400"}>
                      Last: {measurementInfo.lastEntry}
                    </span>
                  </div>
                )}
              </div>
              <span className={cn(
                "font-medium",
                measurementInfo.isOverdue ? "text-amber-600 dark:text-amber-400" : "text-indigo-600 dark:text-indigo-400"
              )}>
                {measurementInfo.nextDue}
              </span>
            </div>
            
            {/* Chart */}
            <KPIChart
              dataPoints={data_points}
              startValue={start_value}
              targetValue={target_value}
              metricFormat={metric_format}
              direction={direction}
              colors={colors}
              goalStartDate={goalStartDate}
              targetDate={targetDate}
              height={220}
            />
            
            {/* Progress Summary Bar */}
            <div className={cn(
              "flex items-center justify-between p-3 rounded-lg",
              colors.bgTertiary || "bg-gray-50 dark:bg-gray-800/50"
            )}>
              <div className="flex items-center gap-6">
                <div>
                  <span className={cn("text-xs", colors.textMuted || "text-gray-500")}>Start</span>
                  <p className={cn("font-semibold", colors.textPrimary || "text-gray-900 dark:text-white")}>
                    {formatValue(start_value, metric_format)}
                  </p>
                </div>
                <div className={cn("text-lg", colors.textMuted || "text-gray-400")}>→</div>
                <div>
                  <span className={cn("text-xs", colors.textMuted || "text-gray-500")}>Current</span>
                  <p className="font-semibold text-indigo-600 dark:text-indigo-400">
                    {formatValue(latestValue, metric_format)}
                  </p>
                </div>
                <div className={cn("text-lg", colors.textMuted || "text-gray-400")}>→</div>
                <div>
                  <span className={cn("text-xs", colors.textMuted || "text-gray-500")}>Target</span>
                  <p className="font-semibold text-green-600 dark:text-green-400">
                    {formatValue(target_value, metric_format)}
                  </p>
                </div>
              </div>
              
              {/* Progress percentage */}
              <div className="text-right">
                <span className={cn("text-2xl font-bold", colors.textPrimary || "text-gray-900 dark:text-white")}>
                  {progressPercent}%
                </span>
                <p className={cn("text-xs", colors.textMuted || "text-gray-500")}>progress</p>
              </div>
            </div>
            
            {/* Full Width Log Data Button */}
            <Button
              onClick={(e) => { e.stopPropagation(); setShowDataModal(true); }}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid={`log-data-btn-${kpi.kpi_id}`}
            >
              <Plus className="w-4 h-4 mr-2" />
              Log Data ({data_points.length} entries)
            </Button>
          </div>
        )}
      </div>
      
      {/* Data Log Modal */}
      <DataLogModal
        isOpen={showDataModal}
        onClose={() => setShowDataModal(false)}
        goalId={goalId}
        kpi={kpi}
        onDataChange={onRefresh}
      />
    </>
  );
};

export default ActiveKPICard;
